import React from 'react';
import PropTypes from 'prop-types';
import Stats from '@strava/ui/Stats';
import Stat from '@strava/ui/Stat';
import { Markup } from 'interweave';
import {
  ActionsKudoNormalSmall,
  ActionsCommentNormalSmall,
  AchievementsTrophyNormalSmall
} from '@strava/icons';
import { color as colorToken } from '@strava/ui-tokens/js';
import I18n from 'utils/I18n';
import { logError } from 'utils/sentry';
import ActivityIcon from 'components/ActivityIcon';
import Button from '@strava/ui/Button';
import { useActivityPublicContext } from '../../ActivityPublicContext';

import styles from './styles.scss';

const ICONS = {
  kudos: {
    Icon: ActionsKudoNormalSmall,
    trackElement: 'kudo',
    modalVariant: 'kudos_comment'
  },
  comments: {
    Icon: ActionsCommentNormalSmall,
    trackElement: 'comment',
    modalVariant: 'kudos_comment'
  },
  achievements: {
    Icon: AchievementsTrophyNormalSmall,
    trackElement: 'achievement_trophy',
    modalVariant: 'generic'
  }
};

const Summary = ({
  name,
  date,
  type,
  detailedType,
  kudosCount,
  commentCount,
  achievementsCount,
  distance,
  elevation,
  time,
  calories
}) => {
  const activityType = type.toLowerCase();
  const translatedActivityType = I18n.t(
    `strava.activities.types_v2.${activityType}`
  );

  const {
    openSignUpModal,
    showSignUpModal,
    track
  } = useActivityPublicContext();
  const iconFillColor = colorToken.colorExtendedNeutralN2;

  const trackClick = (element) => {
    track({
      element,
      action: 'click'
    });
  };

  const icon = (key, count) => {
    if (ICONS[key] === undefined) {
      logError(`invalid icon key '${key}'`);
      return null;
    }

    const { Icon, trackElement, modalVariant } = ICONS[key];
    const title = I18n.t(`strava.activities.show_public.${key}_icon`);

    if (showSignUpModal) {
      return (
        <Button
          variant="icon"
          title={title}
          onClick={() => {
            trackClick(trackElement);
            openSignUpModal(modalVariant);
          }}
          data-cy={`${key}-icon`}
        >
          <Icon fill={iconFillColor} />
          {count}
        </Button>
      );
    }

    return (
      <>
        <Icon
          fill={iconFillColor}
          title={title}
          // we want to track clicks on the icon, even outside of the experiment
          onClick={() => trackClick(trackElement)}
          data-cy={`${key}-icon`}
        />
        {count}
      </>
    );
  };

  return (
    <div className={styles.summaryWrapper}>
      <div className={styles.dateType}>
        <span className={styles.typeText}>{translatedActivityType}</span>
        <time className={styles.date} dateTime={date}>
          {date}
        </time>
      </div>

      <div className={styles.headerText}>
        <h1 className={styles.name}>{name}</h1>
        <div className={`app-icon-wrapper ${styles.typeIconWrapper}`}>
          <ActivityIcon
            type={detailedType}
            size="small"
            title={`${translatedActivityType}`}
            className={styles.typeIcon}
          />
        </div>
      </div>

      <ul className={styles.iconListWrapper}>
        <li>{icon('kudos', kudosCount)}</li>
        <li>{icon('comments', commentCount)}</li>
        <li>{icon('achievements', achievementsCount)}</li>
      </ul>

      <Stats listClassName={styles.statsWrapper}>
        <Stat
          value={distance}
          label={I18n.t('strava.activities.show_public.distance')}
          type="stat-xl"
        />
        <Stat
          value={<Markup noWrap={true} content={time} />}
          label={I18n.t('strava.activities.show_public.time')}
          type="stat-xl"
        />
        {elevation && (
          <Stat
            value={elevation}
            label={I18n.t('strava.activities.show_public.elevation')}
            type="stat-xl"
          />
        )}
        {calories && (
          <Stat
            value={Math.ceil(calories)}
            label={I18n.t('strava.activities.show_public.calories')}
            type="stat-xl"
          />
        )}
      </Stats>
    </div>
  );
};

Summary.defaultProps = {
  elevation: null,
  calories: null
};

Summary.propTypes = {
  name: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  detailedType: PropTypes.string.isRequired,
  kudosCount: PropTypes.number.isRequired,
  commentCount: PropTypes.number.isRequired,
  achievementsCount: PropTypes.number.isRequired,
  distance: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  elevation: PropTypes.string,
  calories: PropTypes.number
};

export default Summary;
